import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { APP_CONFIG } from '../../../core/config/app-config.value';
import type { AssetPhoto } from '../../../core/models/asset.model';
import type {
  AssetRowDto,
  CreateAssetRequestDto,
  PageResultDto,
  ProblemDetailsDto,
} from './inventory.dto';

/** Échec métier renvoyé par l'API, réduit à ce que l'écran affiche : le code de la règle et son message. */
export interface ApiFailure {
  readonly code: string;
  readonly message: string;
}

/**
 * Appels HTTP du module Inventory. Cette classe ne connaît que les contrats de l'API : aucune
 * notion de modèle d'écran, aucun signal, aucune règle métier. Elle est la seule à savoir qu'il
 * existe un réseau.
 */
@Injectable({ providedIn: 'root' })
export class InventoryHttpGateway {
  private readonly http = inject(HttpClient);
  private readonly assets = `${APP_CONFIG.apiBaseUrl}/inventory/assets`;

  /** `GET /api/inventory/assets` — UC-02, réponse paginée (contrat commun `PageResult`). */
  list(
    search: string | null,
    status: string | null,
    page: number,
    pageSize: number,
  ): Promise<PageResultDto<AssetRowDto>> {
    const params: Record<string, string> = { page: String(page), pageSize: String(pageSize) };
    if (search) params['search'] = search;
    if (status) params['status'] = status;

    return firstValueFrom(this.http.get<PageResultDto<AssetRowDto>>(this.assets, { params }));
  }

  /** `POST /api/inventory/assets` — UC-01. Renvoie l'identité attribuée par le serveur. */
  create(request: CreateAssetRequestDto): Promise<{ id: string }> {
    return firstValueFrom(this.http.post<{ id: string }>(this.assets, request));
  }

  /** `POST /api/inventory/assets/{id}/return` — UC-04. */
  async returnAsset(id: string): Promise<void> {
    await firstValueFrom(this.http.post<void>(`${this.assets}/${id}/return`, null));
  }

  /**
   * `POST /api/inventory/assets/{id}/photos` — RM-PIN-122. Le corps est un `multipart/form-data`
   * dont le champ s'appelle `file`, comme le paramètre `IFormFile` du controller.
   */
  async attachPhoto(assetId: string, photo: AssetPhoto): Promise<void> {
    const body = new FormData();
    body.append('file', await dataUrlToBlob(photo.url, photo.mimeType), photo.fileName);

    await firstValueFrom(this.http.post<{ id: string }>(`${this.assets}/${assetId}/photos`, body));
  }

  /** URL de service des octets d'une photo, à poser telle quelle dans un `src`. */
  photoUrl(assetId: string, photoId: string): string {
    return `${this.assets}/${assetId}/photos/${photoId}`;
  }
}

/**
 * Le formulaire garde les photos sous forme de data URL, produite par le navigateur avant tout
 * appel réseau. Le téléversement attend des octets : on repasse par un `Blob`.
 */
async function dataUrlToBlob(dataUrl: string, mimeType: string): Promise<Blob> {
  const response = await fetch(dataUrl);
  const blob = await response.blob();

  return blob.type ? blob : new Blob([await blob.arrayBuffer()], { type: mimeType });
}

/**
 * Traduit une réponse d'erreur en échec métier affichable. Le code de la règle voyage dans
 * l'extension `code` du ProblemDetails (`ApiControllerBase`) : c'est lui qu'on veut à l'écran,
 * pas « 422 Unprocessable Entity ».
 */
export function toApiFailure(error: unknown, fallbackMessage: string): ApiFailure {
  if (error instanceof HttpErrorResponse) {
    const problem = error.error as ProblemDetailsDto | null;

    if (problem?.code) {
      return { code: problem.code, message: problem.title ?? problem.detail ?? fallbackMessage };
    }

    // Pas de ProblemDetails : la réponse ne vient pas du module. En développement, le proxy du
    // serveur Nx traduit une API arrêtée en 500 — d'où un message qui nomme d'abord cette cause,
    // de loin la plus fréquente, plutôt que d'envoyer chercher un bug côté serveur.
    if (error.status === 0 || error.status >= 500) {
      return {
        code: error.status === 0 ? 'API-INJOIGNABLE' : `HTTP-${error.status}`,
        message:
          "L'API n'a pas répondu. Vérifiez qu'elle tourne sur http://localhost:5199 " +
          '(`dotnet run --project src/Unipros.Pin.Api`).',
      };
    }

    return { code: `HTTP-${error.status}`, message: problem?.title ?? fallbackMessage };
  }

  return { code: 'ERREUR-INATTENDUE', message: fallbackMessage };
}
